import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import SingleOrderItem from '../../../component/pages/orders/singleOrder/item/item';
import ApiService from '../../../services/apiService';
import Progress from '../../../component/pages/orders/singleOrder/Progress/progress';
import PaySection from '../../../component/pages/orders/singleOrder/paymentSection/paymentSection';
import { OrderType } from '../../../../Enums/config';
import { BiReceipt } from 'react-icons/bi';
import { FiArrowLeft, FiPackage, FiUser, FiFileText, FiCreditCard } from 'react-icons/fi';
import './enhancedOrderDetails.css';

const getPaymentNetAmount = (p) => {
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const candidates = [p?.paymentAmount, p?.price, p?.amount, p?.amountCollected];
  for (const c of candidates) {
    const n = num(c);
    if (n > 0) return n;
  }
  const paid = num(p?.amountPaid);
  if (paid > 0) {
    const change = num(p?.change);
    return Math.max(0, paid - change);
  }
  return 0;
};

/** Gross of non-refund rows only — can overstate net after partial refunds */
const getTotalPaidFromPaymentRows = (rows = []) =>
  rows
    .filter((p) => !p?.isRefund)
    .map((p) => getPaymentNetAmount(p))
    .filter((v) => Number.isFinite(v) && v > 0)
    .reduce((s, v) => s + v, 0);

/**
 * Net cash/card applied: payments minus refunds (partial refund / PARTIALLY REFUNDED).
 * Uses signed `amount` when API sends it; otherwise subtracts rows marked `isRefund`.
 */
const getNetPaidFromPaymentRows = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return 0;
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  let sum = 0;
  for (const p of rows) {
    const direct = num(p?.amount);
    if (direct !== 0) {
      sum += direct;
      continue;
    }
    const isRefund = p?.isRefund === true || p?.isRefund === 1;
    let mag = 0;
    for (const c of [p?.paymentAmount, p?.price, p?.amountCollected]) {
      const n = num(c);
      if (n !== 0) {
        mag = Math.abs(n);
        break;
      }
    }
    if (mag === 0) {
      const paid = num(p?.amountPaid);
      if (paid > 0) mag = Math.max(0, paid - num(p?.change));
    }
    if (isRefund) sum -= mag;
    else sum += mag;
  }
  return Number(sum.toFixed(2));
};

const getLineUnitPrice = (item) => Number(item?.menuItemPrice ?? item?.itemAmount ?? item?.itemPrice ?? 0);

/** Per-line net (matches cashier line math) */
const lineNetTotal = (item) => {
  if (item.refunded || item.voidItem) return 0;
  const qty = Number(item.quantity || 0);
  const price = getLineUnitPrice(item);
  const discRaw = Number(item.menuItemDiscount || 0);
  const discType = String(item.menuItemDiscountType || item.MenuItemDiscountType || 'amount').toLowerCase();
  const discPU = discType.includes('percent') ? (price * discRaw) / 100 : discRaw;
  return Math.max(0, price - discPU) * qty;
};

/** Active lines only — void/refunded lines excluded from order totals */
const computeActiveLineNetTotal = (items = []) => {
  let sum = 0;
  for (const item of items) {
    sum += lineNetTotal(item);
  }
  return Number(sum.toFixed(2));
};

const sumGrossActiveLines = (items = []) => {
  let s = 0;
  for (const item of items) {
    if (item.refunded || item.voidItem) continue;
    s += getLineUnitPrice(item) * Number(item.quantity || 0);
  }
  return Number(s.toFixed(2));
};

/**
 * API often returns both an ACTIVE row and a VOID row for the same unit.
 * Void rows may use a different itemIndex than the active row, so match by itemId + qty + unit price.
 * Remove at most one active line per void line with the same key (handles duplicate SKU lines).
 */
const consolidateVoidMirrorLines = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const voidMirrorKey = (it) => `${it.itemId}|${Number(it.quantity || 0)}|${getLineUnitPrice(it).toFixed(2)}`;
  const voidCountByKey = new Map();
  for (const it of items) {
    if (it?.voidItem) {
      const k = voidMirrorKey(it);
      voidCountByKey.set(k, (voidCountByKey.get(k) || 0) + 1);
    }
  }
  const activeRemovedByKey = new Map();
  return items.filter((it) => {
    if (it?.refunded || it?.voidItem) return true;
    const k = voidMirrorKey(it);
    const voidN = voidCountByKey.get(k) || 0;
    const removed = activeRemovedByKey.get(k) || 0;
    if (voidN > 0 && removed < voidN) {
      activeRemovedByKey.set(k, removed + 1);
      return false;
    }
    return true;
  });
};

const normalizeStr = (v) =>
  String(v ?? '')
    .trim()
    .toLowerCase();

/** Per-line net for void/replacement valuation (same as saleHistory `computeSingleLineNet`) */
const computeSingleLineNet = (item) => {
  if (!item) return 0;
  const qty = Number(item.quantity || 0);
  const price = Number(item.menuItemPrice || item.itemAmount || item.itemPrice || 0);
  const discRaw = Number(item.menuItemDiscount ?? 0);
  const discType = String(item.menuItemDiscountType || item.MenuItemDiscountType || 'amount').toLowerCase();
  const discPU = discType.includes('percent') ? (price * discRaw) / 100 : discRaw;
  return Math.max(0, price - discPU) * qty;
};

/**
 * Full amount paid after replacement when GET_PAYMENT only returns the first tender (missing top-up row).
 * Mirrors saleHistory.js → resolveReplacementTotalPaid.
 */
const resolveReplacementTotalPaid = (paidFromApi, lineTotal, diff) => {
  let p = Number(paidFromApi);
  const t = Number(lineTotal);
  const d = Number(diff);
  if (!Number.isFinite(t) || t <= 0) return Number.isFinite(p) && p > 0 ? Number(p.toFixed(2)) : 0;
  if (!Number.isFinite(p) || p < 0) p = 0;
  if (Math.abs(p - t) < 0.05) return Number(t.toFixed(2));
  if (d > 0 && Math.abs(p + d - t) < 0.05) return Number(t.toFixed(2));
  return Number(Math.max(p, t).toFixed(2));
};

const computeReplacementDiff = (items = []) => {
  const voidLines = items.filter((i) => i.voidItem || normalizeStr(i?.kitchenInstructions).startsWith('void'));
  const replLines = items.filter((i) =>
    String(i?.kitchenInstructions || '')
      .toLowerCase()
      .includes('replacement'),
  );
  if (!voidLines.length || !replLines.length) return 0;
  const voidVal = voidLines.reduce((s, i) => s + computeSingleLineNet(i), 0);
  const replVal = replLines.reduce((s, i) => s + computeSingleLineNet(i), 0);
  return Number((replVal - voidVal).toFixed(2));
};

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Same as saleHistory `groupOrdersForGrid`: duplicate GET_ORDERS rows for one sale have
 * paymentAmount / amountPaid that must be summed (e.g. original + replacement top-up).
 */
const sumPaymentAcrossOrderSnapshots = (rows = []) => {
  let sum = 0;
  for (const o of rows) {
    sum += safeNum(o?.paymentAmount ?? o?.amountPaid ?? o?.amountCollected ?? 0);
  }
  return Number(sum.toFixed(2));
};

const CashOrderDetails = ({ Currency }) => {
  const [orderDetails, setOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [paymentNetTotal, setPaymentNetTotal] = useState(0);
  /** { paidSum, latestRow } from GET_ORDERS?id= — matches grid merge logic */
  const [mergedOrderFromApi, setMergedOrderFromApi] = useState(null);

  const { state } = useLocation();
  const itemData = state;

  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  useEffect(() => {
    if (itemData && typeof itemData === 'object' && itemData.id) {
      setOrders(itemData);
      setLoading(false);
    } else if (itemData && typeof itemData === 'string') {
      getItemDetails();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemData]);

  const orderIdForPayments = orderDetails?.id ?? orderDetails?.originalId;

  useEffect(() => {
    if (!orderIdForPayments) {
      setPaymentNetTotal(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const api = new ApiService();
        const res = await api.getApiParamater('GET_PAYMENT', `?orderId=${orderIdForPayments}`);
        const rows = res?.data?.result || [];
        const sum = getNetPaidFromPaymentRows(rows);
        if (!cancelled) setPaymentNetTotal(sum);
      } catch {
        if (!cancelled) setPaymentNetTotal(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderIdForPayments]);

  useEffect(() => {
    if (!orderIdForPayments) {
      setMergedOrderFromApi(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const api = new ApiService();
        const res = await api.getApiParamater('GET_ORDERS', `?id=${orderIdForPayments}`);
        const rows = res?.data?.result;
        if (!Array.isArray(rows) || rows.length === 0) {
          if (!cancelled) setMergedOrderFromApi(null);
          return;
        }
        const paidSum = sumPaymentAcrossOrderSnapshots(rows);
        const latestRow = rows.reduce((a, b) => (new Date(a.orderDate || 0) > new Date(b.orderDate || 0) ? a : b));
        if (!cancelled) setMergedOrderFromApi({ paidSum, latestRow });
      } catch {
        if (!cancelled) setMergedOrderFromApi(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderIdForPayments]);

  const getItemDetails = async () => {
    try {
      setLoading(true);
      const apiService = new ApiService();
      const itemNumber = itemData.replace(/^ORD-/i, '');
      const parameter = `?id=${itemNumber}`;
      const res = await apiService.getApiParamater('GET_ORDERS', parameter);
      setOrders(res?.data?.result?.[0] || {});
    } catch (error) {
      console.error('Error fetching order details:', error);
      setOrders({});
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '0.00';
    const num = Number(amount);
    return Number.isFinite(num) ? num.toFixed(2) : '0.00';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getOrderTypeDisplay = (type) => {
    if (type === OrderType.Delivery || type === 2) return { text: 'Delivery', icon: '🚚', className: 'badge-delivery' };
    if (type === OrderType.DineIn || type === 0) return { text: 'Dine In', icon: '🍽️', className: 'badge-dinein' };
    if (type === OrderType.OnlineOrder || type === 3) return { text: 'Online Order', icon: '💻', className: 'badge-online' };
    return { text: 'Takeaway', icon: '🥡', className: 'badge-takeaway' };
  };

  const getPaymentTypeDisplay = (method) => {
    const val = typeof method === 'string' ? method.trim().toLowerCase() : method;

    if (val === 1 || val === 'cash') return { text: 'Cash', icon: '💵', className: 'payment-cash' };
    if (val === 2 || val === 'card') return { text: 'Card', icon: '💳', className: 'payment-card' };
    if (val === 3 || val === 'stripe') return { text: 'Stripe', icon: '💻', className: 'payment-stripe' };
    if (val === 'account') return { text: 'Account', icon: '🏦', className: 'payment-account' };

    return { text: String(method ?? 'Unknown'), icon: '❓', className: 'payment-unknown' };
  };

  const getOrderStatusDisplay = (status) => {
    switch (status) {
      case 0:
        return { text: 'New Order', className: 'status-new-order', color: '#6E5199', bg: '#E8E2FF' };
      case 1:
        return { text: 'Accepted', className: 'status-accepted', color: '#28a745', bg: '#d4edda' };
      case 2:
        return { text: 'Preparing', className: 'status-preparing', color: '#856404', bg: '#fff3cd' };
      case 3:
        return { text: 'Ready For Delivery', className: 'status-ready-delivery', color: '#17a2b8', bg: '#d1ecf1' };
      case 4:
        return { text: 'Ready For Pickup', className: 'status-ready-pickup', color: '#17a2b8', bg: '#d1ecf1' };
      case 5:
        return { text: 'Out For Delivery', className: 'status-out-delivery', color: '#fd7e14', bg: '#ffeaa7' };
      case 6:
        return { text: 'Completed', className: 'status-completed', color: '#28a745', bg: '#d4edda' };
      case 7:
        return { text: 'Canceled', className: 'status-canceled', color: '#dc3545', bg: '#f8d7da' };
      case 8:
        return { text: 'On-Hold', className: 'status-on-hold', color: '#6c757d', bg: '#e2e3e5' };
      case 9:
        return { text: 'Refund Initiated', className: 'status-refund-initiated', color: '#fd7e14', bg: '#ffeaa7' };
      case 10:
        return { text: 'Refunded', className: 'status-refunded', color: '#dc3545', bg: '#f8d7da' };
      case 11:
        return { text: 'Payment Partially Refunded', className: 'status-partial-refund', color: '#fd7e14', bg: '#ffeaa7' };
      case 12:
        return { text: 'Payment Partially Paid', className: 'status-partial-paid', color: '#6E5199', bg: '#E8E2FF' };
      default:
        return { text: 'Unknown', className: 'status-unknown', color: '#6c757d', bg: '#e2e3e5' };
    }
  };

  const getItemStatusStyle = (item) => {
    if (item?.refunded) {
      return {
        borderColor: '#dc3545',
        borderWidth: '2px',
        borderStyle: 'solid',
        backgroundColor: '#f8d7da',
        opacity: 0.8,
      };
    }
    if (item?.voidItem) {
      return {
        borderColor: '#ffc107',
        borderWidth: '2px',
        borderStyle: 'solid',
        backgroundColor: '#fff3cd',
        opacity: 0.8,
      };
    }
    return {
      borderColor: '#e9ecef',
      borderWidth: '1px',
      borderStyle: 'solid',
      backgroundColor: '#ffffff',
    };
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!orderDetails || Object.keys(orderDetails).length === 0) {
    return (
      <div className="enhanced-order-details">
        <div className="enhanced-container">
          <div className="enhanced-card">
            <div className="enhanced-card-body">
              <div className="empty-state">
                <FiPackage className="empty-icon" />
                <p>No order details found</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /** Merge latest GET_ORDERS snapshot + summed paid (same as sale history grid). */
  const orderForTotals = (() => {
    const base = orderDetails;
    const m = mergedOrderFromApi;
    if (!m) return base;
    const { paidSum, latestRow: snap } = m;
    const out = snap ? { ...base, ...snap } : { ...base };
    if (snap && Array.isArray(snap.orderItems) && snap.orderItems.length > 0) {
      out.orderItems = snap.orderItems;
    }
    if (snap?.orderCombos) out.orderCombos = snap.orderCombos;
    if (snap?.orderAddOns) out.orderAddOns = snap.orderAddOns;
    // Never downgrade below sale-history row / navigation state when ?id= returns one snapshot only.
    out.paymentAmount = Math.max(safeNum(out.paymentAmount), paidSum);
    out.amountCollected = Math.max(safeNum(out.amountCollected), paidSum);
    return out;
  })();

  const orderType = getOrderTypeDisplay(orderForTotals?.orderType);
  const orderStatus = getOrderStatusDisplay(orderForTotals?.orderStatus);

  const paymentMethods =
    Array.isArray(orderForTotals?.paymentMethods) && orderForTotals.paymentMethods.length
      ? orderForTotals.paymentMethods
      : orderForTotals?.paymentMethod
        ? [orderForTotals.paymentMethod]
        : orderForTotals?.paymentType
          ? [orderForTotals.paymentType]
          : [];

  const rawOrderItems = orderForTotals?.orderItems || [];
  /** Hides duplicate ACTIVE rows when API also sent a matching VOID row */
  const displayOrderItems = consolidateVoidMirrorLines(rawOrderItems);

  const totalItems = displayOrderItems.length + (orderForTotals?.orderCombos?.length || 0) + (orderForTotals?.orderAddOns?.length || 0);

  const itemCounts = {
    total: displayOrderItems.length,
    refunded: displayOrderItems.filter((item) => item?.refunded).length,
    void: displayOrderItems.filter((item) => item?.voidItem).length,
    active: displayOrderItems.filter((item) => !item?.refunded && !item?.voidItem).length,
  };

  const totalDiscount = Number(orderForTotals?.discount || 0);
  const tax = Number(orderForTotals?.calculatedTax || 0);
  const headerOrderAmount = Number(orderForTotals?.orderAmount || 0);

  const activeLineTotal = computeActiveLineNetTotal(displayOrderItems);
  const grossActiveLines = sumGrossActiveLines(displayOrderItems);

  /** Subtotal from lines / header — may be ex-tax or tax-inclusive depending on store/API */
  const subtotalExTax = activeLineTotal > 0 ? activeLineTotal : headerOrderAmount;

  const amountCollected = Number(orderForTotals?.amountCollected || 0);
  const amountReturned = Number(orderForTotals?.amountReturned || 0);
  const amountRefunded = Number(orderForTotals?.amountRefunded || 0);

  /**
   * Sale history grid: merged `paymentAmount` / replacement top-ups.
   * `paymentNetTotal` from GET_PAYMENT uses `getNetPaidFromPaymentRows` (net of refunds).
   * For PARTIALLY REFUNDED (paymentStatus 9 / 11), do not Math.max with snapshot gross — that overstates vs grid.
   */
  const gridPaidSum = mergedOrderFromApi?.paidSum ?? 0;
  const headerAndGridPaid = Math.max(
    Number(orderForTotals?.paymentAmount ?? 0),
    Number(orderForTotals?.amountPaid ?? 0),
    Number(orderForTotals?.amountCollected ?? 0),
    gridPaidSum,
  );
  const ps = Number(orderForTotals?.paymentStatus);
  const isPartiallyRefundedPayment = ps === 9 || ps === 11;

  const paymentNetResolved = isPartiallyRefundedPayment
    ? paymentNetTotal > 0.005
      ? paymentNetTotal
      : headerAndGridPaid
    : Math.max(paymentNetTotal, headerAndGridPaid);

  const originalTotal = grossActiveLines > 0 ? grossActiveLines : headerOrderAmount + totalDiscount;

  /**
   * Grand total: avoid double-counting tax when line sums / subtotal are already tax-inclusive
   * (paid amount matches subtotal, but subtotal + calculatedTax overshoots).
   * When ex-tax + tax matches amount paid, keep additive total.
   */
  const tol = 0.5;
  const additiveTotal = Math.max(0, Number((subtotalExTax + tax).toFixed(2)));
  const paidRef = paymentNetResolved > 0 ? paymentNetResolved : amountCollected;
  const additiveMatchesPaid =
    tax <= 0.01 ||
    Math.abs(additiveTotal - amountCollected) <= tol ||
    (paidRef > 0 && Math.abs(additiveTotal - paidRef) <= tol) ||
    (headerOrderAmount > 0 && tax > 0.01 && Math.abs(headerOrderAmount + tax - amountCollected) <= tol);

  const taxAlreadyInSubtotal = tax > 0.01 && !additiveMatchesPaid && paidRef > 0 && Math.abs(subtotalExTax - paidRef) <= tol;

  const grandTotal = taxAlreadyInSubtotal ? Math.max(0, Number(subtotalExTax.toFixed(2))) : additiveTotal;

  /** Subtotal row = tax-inclusive final cart value; Total must not add replacement tender again on top of this. */
  const subtotalIsTaxInclusiveFinal =
    taxAlreadyInSubtotal || (tax <= 0.01 && Math.abs(Number(grandTotal.toFixed(2)) - Number(subtotalExTax.toFixed(2))) <= tol);

  const hasReplacementOrVoid =
    displayOrderItems.some((i) => i?.voidItem) ||
    displayOrderItems.some((i) =>
      String(i?.kitchenInstructions || '')
        .toLowerCase()
        .includes('replacement'),
    );

  /** When void/replacement line math diverges from API order total, follow orderAmount (matches receipt / backend). */
  const apiOrderTotal = Number(orderForTotals?.orderAmount ?? 0);
  const apiCollected = Number(orderForTotals?.amountCollected ?? 0);
  /** After void/replacement, header orderAmount + amountCollected often stay at the pre-swap tender — do not snap summary to that stale pair. */
  const apiOrderMatchesStaleTender = apiOrderTotal > 0 && Math.abs(apiOrderTotal - apiCollected) < 0.05 && Math.abs(grossActiveLines - apiOrderTotal) > 1;

  let summaryGrandTotal = grandTotal;
  let summarySubtotalExTax = subtotalExTax;
  let summaryItemsGross = originalTotal;
  if (
    apiOrderTotal > 0 &&
    Math.abs(apiOrderTotal - apiCollected) < 0.05 &&
    Math.abs(summaryGrandTotal - apiOrderTotal) > 0.5 &&
    !(hasReplacementOrVoid && apiOrderMatchesStaleTender)
  ) {
    summaryGrandTotal = Number(apiOrderTotal.toFixed(2));
    if (tax > 0.01 && !taxAlreadyInSubtotal) {
      summarySubtotalExTax = Math.max(0, Number((apiOrderTotal - tax).toFixed(2)));
    } else if (tax <= 0.01) {
      summarySubtotalExTax = summaryGrandTotal;
    }
  }
  if (apiOrderTotal > 0 && Math.abs(grossActiveLines - apiOrderTotal) > 1 && Math.abs(apiOrderTotal - apiCollected) < 0.05 && !hasReplacementOrVoid) {
    summaryItemsGross = apiOrderTotal;
  }
  if (hasReplacementOrVoid) {
    summaryItemsGross = Math.max(summaryItemsGross, grossActiveLines, activeLineTotal);
  }
  const refundShownAsAdjustment = amountRefunded > 0 && itemCounts.refunded === 0 && hasReplacementOrVoid;

  /**
   * Snapshot of order totals from **lines + tax** before payment/replacement inference.
   * Used to detect double-counting: payment history may sum initial tender + upgrade while active lines already show the upgraded cart only.
   */
  const lineSummarySnapshot = {
    grand: summaryGrandTotal,
    sub: summarySubtotalExTax,
    items: summaryItemsGross,
  };

  /** Infer total tender when API payment rows omit the replacement top-up (same idea as receipt / saleHistory). */
  const replacementDiff = computeReplacementDiff(displayOrderItems);
  const finalOrderTotalForPaid = Math.max(
    summaryGrandTotal,
    apiOrderTotal,
    additiveTotal,
    activeLineTotal + tax,
    replacementDiff > 0 && paymentNetResolved > 0 ? paymentNetResolved + replacementDiff : 0,
    hasReplacementOrVoid && paymentNetResolved > 0 ? paymentNetResolved : 0,
  );
  /**
   * Subtotal is the tax-inclusive final sale — paid amount must match it; do not resolve replacement again on top.
   */
  let effectivePaymentTotal = paymentNetResolved;
  if (hasReplacementOrVoid && subtotalIsTaxInclusiveFinal && summarySubtotalExTax > 0) {
    effectivePaymentTotal = Number(summarySubtotalExTax.toFixed(2));
  } else if (replacementDiff > 0.005 && paymentNetResolved > 0 && finalOrderTotalForPaid > 0) {
    effectivePaymentTotal = resolveReplacementTotalPaid(paymentNetResolved, finalOrderTotalForPaid, replacementDiff);
  }

  if (hasReplacementOrVoid && finalOrderTotalForPaid > summaryGrandTotal + 0.5 && !subtotalIsTaxInclusiveFinal) {
    summaryGrandTotal = Number(finalOrderTotalForPaid.toFixed(2));
    if (tax > 0.01 && !taxAlreadyInSubtotal) {
      summarySubtotalExTax = Math.max(0, Number((summaryGrandTotal - tax).toFixed(2)));
    } else if (tax <= 0.01) {
      summarySubtotalExTax = summaryGrandTotal;
    }
    summaryItemsGross = Math.max(summaryItemsGross, grossActiveLines, activeLineTotal);
  }

  /**
   * Payment rows = cart total + replacement upgrade delta, but void/replacement line math already rolled the upgrade into active lines.
   * Then tender sum (line + diff) double-counts vs subtotal — align Total + paid to line snapshot.
   * Require snapshot to match additive line total so we don't snap down when header lines are stale vs real cart.
   */
  const snapshotMatchesLineTotals =
    Math.abs(lineSummarySnapshot.grand - additiveTotal) < 2.5 || Math.abs(lineSummarySnapshot.grand - Number(grandTotal.toFixed(2))) < 2.5;

  const paymentLooksLikeLinePlusReplacementDiff =
    hasReplacementOrVoid &&
    replacementDiff > 0.005 &&
    lineSummarySnapshot.grand > 0 &&
    paymentNetResolved > lineSummarySnapshot.grand + 0.5 &&
    Math.abs(paymentNetResolved - lineSummarySnapshot.grand - replacementDiff) < 2.5 &&
    snapshotMatchesLineTotals;

  if (paymentLooksLikeLinePlusReplacementDiff) {
    summaryGrandTotal = lineSummarySnapshot.grand;
    summarySubtotalExTax = lineSummarySnapshot.sub;
    summaryItemsGross = lineSummarySnapshot.items;
    effectivePaymentTotal = lineSummarySnapshot.grand;
  }

  /**
   * Same as above: Subtotal row = final tax-inclusive cart; Total must equal Subtotal (not + replacement tender).
   */
  if (hasReplacementOrVoid && subtotalIsTaxInclusiveFinal && summarySubtotalExTax > 0) {
    const inclusiveFinal = Number(summarySubtotalExTax.toFixed(2));
    summaryGrandTotal = inclusiveFinal;
    effectivePaymentTotal = inclusiveFinal;
  }

  const amountGivenDisplay = effectivePaymentTotal > 0 ? effectivePaymentTotal : amountCollected;
  const netPaid = effectivePaymentTotal > 0 ? Number(effectivePaymentTotal.toFixed(2)) : Number(Math.max(0, amountCollected - amountReturned).toFixed(2));

  return (
    <div className="enhanced-order-details">
      <div className="enhanced-container">
        {/* Header Section */}
        <div className="enhanced-card">
          <div className="enhanced-card-body">
            <div className="order-header">
              <div className="w-25">
                <button
                  onClick={goBack}
                  className="btn btn-sm"
                  style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    padding: '6px 12px',
                    backgroundColor: '#6E5199',
                    borderColor: '#6E5199',
                    color: '#fff',
                    borderRadius: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                  title="Go back"
                >
                  <FiArrowLeft />
                  Back
                </button>
              </div>

              <div className="order-header-main">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <h1 className="order-title">Order Details</h1>
                  <div className="order-meta">
                    <span className="order-number">Order {orderDetails?.id}</span>
                    <span className={`status-badge ${orderStatus.className}`}>{orderStatus.text}</span>
                    <span className={`order-type-badge ${orderType.className}`} title="Order Type">
                      <span style={{ marginRight: 6 }}>{orderType.icon}</span>
                      {orderType.text}
                    </span>
                  </div>
                  <div className="order-date">
                    <small>Order Date: {formatDate(orderDetails?.orderDate)}</small>
                  </div>
                </div>

                <div className="order-total">
                  <p className="total-label">Total</p>
                  <p className="total-amount">
                    {Currency?.currency?.code} {formatCurrency(summaryGrandTotal)}
                  </p>

                  {amountRefunded > 0 && (
                    <p className="refunded-amount" style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                      {refundShownAsAdjustment ? 'Adjustment' : 'Refunded'}: {Currency?.currency?.code} {formatCurrency(amountRefunded)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Section */}
        <div className="enhanced-card">
          <div className="enhanced-card-body">
            <Progress val={orderForTotals} />
          </div>
        </div>

        {/* Order Items Section */}
        <div className="enhanced-card">
          <div className="enhanced-card-header">
            <h2 className="section-header">
              <FiPackage className="section-icon text-blue-600" />
              Order Items
              <div className="item-status-badges" style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                <span className="item-count-badge" style={{ backgroundColor: '#6E5199', color: '#fff' }}>
                  Total: {itemCounts.total}
                </span>
                {itemCounts.active > 0 && (
                  <span className="item-count-badge" style={{ backgroundColor: '#28a745', color: '#fff' }}>
                    Active: {itemCounts.active}
                  </span>
                )}
                {itemCounts.refunded > 0 && (
                  <span className="item-count-badge" style={{ backgroundColor: '#dc3545', color: '#fff' }}>
                    Refunded: {itemCounts.refunded}
                  </span>
                )}
                {itemCounts.void > 0 && (
                  <span className="item-count-badge" style={{ backgroundColor: '#ffc107', color: '#212529' }}>
                    Void: {itemCounts.void}
                  </span>
                )}
              </div>
            </h2>
          </div>

          <div className="enhanced-card-body">
            <div className="items-container">
              {displayOrderItems.map((val, key) => (
                <div key={key} className="mb-3">
                  <div
                    className="order-item-card"
                    style={{
                      ...getItemStatusStyle(val),
                      borderRadius: '8px',
                      padding: '16px',
                      position: 'relative',
                      border: '1px solid #e9ecef',
                      backgroundColor: '#fff',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        width: '100%',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4
                          style={{
                            textDecoration: val?.refunded || val?.voidItem ? 'line-through' : 'none',
                            color: val?.refunded ? '#721c24' : val?.voidItem ? '#856404' : '#2c3e50',
                            margin: '0 0 8px 0',
                            fontSize: '16px',
                            fontWeight: '600',
                            lineHeight: '1.3',
                            wordBreak: 'break-word',
                          }}
                        >
                          {val?.menuItemName || `Item ID: ${val?.itemId}`}
                        </h4>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            flexWrap: 'wrap',
                            marginBottom: val?.kitchenInstructions ? '8px' : 0,
                          }}
                        >
                          <span
                            style={{
                              backgroundColor: val?.refunded ? '#f5c6cb' : val?.voidItem ? '#ffeaa7' : '#e9ecef',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: val?.refunded ? '#721c24' : val?.voidItem ? '#856404' : '#495057',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Qty: {val?.quantity}
                          </span>

                          <div style={{ fontSize: '14px' }}>
                            <span style={{ color: '#6c757d', marginRight: '6px' }}>Unit: </span>
                            <span
                              style={{
                                fontWeight: '600',
                                color: val?.refunded ? '#721c24' : val?.voidItem ? '#856404' : '#28a745',
                              }}
                            >
                              {Currency?.currency?.code} {formatCurrency(getLineUnitPrice(val))}
                            </span>
                          </div>

                          {Number(val?.menuItemDiscount || 0) > 0 && (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ color: '#6c757d', marginRight: '6px' }}>Discount:</span>
                              <span style={{ fontWeight: '600', color: '#dc3545' }}>
                                {String(val?.menuItemDiscountType || '')
                                  .toLowerCase()
                                  .includes('percent')
                                  ? `${val?.menuItemDiscount}% per unit`
                                  : `-${Currency?.currency?.code} ${formatCurrency(val?.menuItemDiscount)} per unit`}
                              </span>
                            </div>
                          )}

                          <div style={{ fontSize: '14px' }}>
                            <span style={{ color: '#6c757d', marginRight: '6px' }}>Line total:</span>
                            <span
                              style={{
                                fontWeight: '700',
                                color: val?.refunded ? '#721c24' : val?.voidItem ? '#856404' : '#28a745',
                                fontSize: '15px',
                              }}
                            >
                              {Currency?.currency?.code} {formatCurrency(val?.refunded || val?.voidItem ? 0 : lineNetTotal(val))}
                            </span>
                          </div>
                        </div>

                        {val?.kitchenInstructions && (
                          <div
                            style={{
                              marginTop: '8px',
                              backgroundColor: '#f8f9fa',
                              padding: '6px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              color: '#495057',
                              fontStyle: 'italic',
                              border: '1px solid #e9ecef',
                            }}
                          >
                            <strong>Instructions:</strong> {val?.kitchenInstructions}
                          </div>
                        )}
                      </div>

                      <div style={{ marginLeft: '16px', flexShrink: 0 }}>
                        {val?.refunded && (
                          <span
                            style={{
                              backgroundColor: '#dc3545',
                              color: '#fff',
                              padding: '6px 12px',
                              borderRadius: '16px',
                              fontSize: '11px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            🔄 REFUNDED
                          </span>
                        )}
                        {val?.voidItem && (
                          <span
                            style={{
                              backgroundColor: '#ffc107',
                              color: '#212529',
                              padding: '6px 12px',
                              borderRadius: '16px',
                              fontSize: '11px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            ❌ VOID
                          </span>
                        )}
                        {!val?.refunded && !val?.voidItem && (
                          <span
                            style={{
                              backgroundColor: '#28a745',
                              color: '#fff',
                              padding: '6px 12px',
                              borderRadius: '16px',
                              fontSize: '11px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            ✅ ACTIVE
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {orderForTotals?.orderCombos?.map((combo, key) => (
                <div key={key} className="mb-3">
                  <SingleOrderItem val={combo} orderdata={orderForTotals} Currency={Currency} isCombo={true} />
                </div>
              ))}

              {orderForTotals?.orderAddOns?.map((addOn, key) => (
                <div key={key} className="mb-3">
                  <SingleOrderItem val={addOn} orderdata={orderForTotals} Currency={Currency} isAddOn={true} />
                </div>
              ))}

              {totalItems === 0 && (
                <div
                  className="empty-state"
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#6c757d',
                  }}
                >
                  <FiPackage className="empty-icon" style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>No items found in this order</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Section */}
        <div className="enhanced-card">
          <div className="enhanced-card-header">
            <h2 className="section-header">
              <FiCreditCard className="section-icon text-green-600" />
              Payment Information
            </h2>
          </div>
          <div className="enhanced-card-body">
            <PaySection val={orderForTotals} />
          </div>
        </div>

        <div className="enhanced-card">
          <div className="enhanced-card-header">
            <h2 className="section-header">
              <BiReceipt className="section-icon text-purple-600" />
              Order Summary
            </h2>
          </div>
          <div className="enhanced-card-body">
            <div className="summary-row">
              <span className="summary-label">
                <span style={{ fontSize: '14px', fontWeight: '500' }}>Items Total</span>
              </span>
              <span className="summary-value" style={{ fontSize: '16px', fontWeight: '600', color: '#495057' }}>
                {Currency?.currency?.code} {formatCurrency(summaryItemsGross)}
              </span>
            </div>

            {totalDiscount > 0 && (
              <div className="summary-row">
                <span className="summary-label">
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>Discount</span>
                </span>
                <span className="summary-value" style={{ fontSize: '16px', fontWeight: '600', color: '#dc3545' }}>
                  - {Currency?.currency?.code} {formatCurrency(totalDiscount)}
                </span>
              </div>
            )}

            <div className="summary-row" style={{ paddingTop: '8px', borderTop: '1px dashed #e9ecef' }}>
              <span className="summary-label">
                <span style={{ fontSize: '14px', fontWeight: '600' }}>Subtotal</span>
              </span>
              <span className="summary-value" style={{ fontSize: '16px', fontWeight: '700', color: '#28a745' }}>
                {Currency?.currency?.code} {formatCurrency(summarySubtotalExTax)}
              </span>
            </div>

            {tax > 0.01 && !taxAlreadyInSubtotal && (
              <div className="summary-row">
                <span className="summary-label">
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>Tax</span>
                </span>
                <span className="summary-value" style={{ fontSize: '16px', fontWeight: '600', color: '#495057' }}>
                  {Currency?.currency?.code} {formatCurrency(tax)}
                </span>
              </div>
            )}

            <div
              className="summary-row"
              style={{
                paddingTop: '12px',
                marginTop: '8px',
                borderTop: '2px solid #6E5199',
                backgroundColor: '#f8f9fa',
                marginLeft: '-16px',
                marginRight: '-16px',
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingBottom: '12px',
              }}
            >
              <span className="summary-label">
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#6E5199' }}>Total</span>
              </span>
              <span className="summary-value" style={{ fontSize: '24px', fontWeight: '800', color: '#6E5199' }}>
                {Currency?.currency?.code} {formatCurrency(summaryGrandTotal)}
              </span>
            </div>

            {/* Payment Details Section */}
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #e9ecef' }}>
              <h6 style={{ fontSize: '15px', fontWeight: '700', color: '#495057', marginBottom: '12px' }}>Payment</h6>

              {(amountCollected > 0 || effectivePaymentTotal > 0) && (
                <div className="summary-row">
                  <span className="summary-label">
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Amount Given</span>
                  </span>
                  <span className="summary-value" style={{ fontSize: '16px', fontWeight: '600', color: '#17a2b8' }}>
                    {Currency?.currency?.code} {formatCurrency(amountGivenDisplay)}
                  </span>
                </div>
              )}

              {/* Change Returned */}
              {amountReturned > 0 && (
                <div className="summary-row">
                  <span className="summary-label">
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>Change Returned</span>
                  </span>
                  <span className="summary-value" style={{ fontSize: '16px', fontWeight: '600', color: '#fd7e14' }}>
                    - {Currency?.currency?.code} {formatCurrency(amountReturned)}
                  </span>
                </div>
              )}

              {/* Net Payment Received */}
              {(amountCollected > 0 || effectivePaymentTotal > 0 || netPaid > 0) && (
                <div
                  className="summary-row"
                  style={{
                    paddingTop: '8px',
                    borderTop: '1px dashed #e9ecef',
                    backgroundColor: '#e8f5e9',
                    marginLeft: '-16px',
                    marginRight: '-16px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingBottom: '8px',
                  }}
                >
                  <span className="summary-label">
                    <span style={{ fontSize: '15px', fontWeight: '700', color: '#155724' }}>Net Payment Received</span>
                  </span>
                  <span className="summary-value" style={{ fontSize: '18px', fontWeight: '800', color: '#28a745' }}>
                    {Currency?.currency?.code} {formatCurrency(netPaid)}
                  </span>
                </div>
              )}

              {effectivePaymentTotal > 0 &&
                amountCollected > 0 &&
                effectivePaymentTotal > amountCollected + 0.5 &&
                hasReplacementOrVoid &&
                !paymentLooksLikeLinePlusReplacementDiff && (
                  <div
                    className="summary-row"
                    style={{
                      fontSize: '12px',
                      color: '#495057',
                      lineHeight: 1.4,
                      paddingBottom: '8px',
                    }}
                  >
                    Payments total {Currency?.currency?.code} {formatCurrency(effectivePaymentTotal)}; order total after replacement is{' '}
                    {Currency?.currency?.code} {formatCurrency(summaryGrandTotal)}. The extra <strong>replacement top-up</strong> of {Currency?.currency?.code}{' '}
                    {formatCurrency(Number((effectivePaymentTotal - amountCollected).toFixed(2)))} is the additional cash collected for the price difference
                    (voided line removed, new item priced).
                  </div>
                )}

              {/* Amount Refunded */}
              {amountRefunded > 0 && (
                <div
                  className="summary-row"
                  style={{
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: '2px solid #dc3545',
                    backgroundColor: '#f8d7da',
                    marginLeft: '-16px',
                    marginRight: '-16px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    paddingBottom: '12px',
                  }}
                >
                  <span className="summary-label">
                    <span style={{ fontSize: '15px', fontWeight: '700', color: '#721c24' }}>
                      {refundShownAsAdjustment ? '⚠️ Balance adjustment (replacement)' : '⚠️ Amount Refunded'}
                    </span>
                  </span>
                  <span className="summary-value" style={{ fontSize: '18px', fontWeight: '800', color: '#dc3545' }}>
                    - {Currency?.currency?.code} {formatCurrency(amountRefunded)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Cards Grid */}
        <div className="info-grid">
          {/* Order Information Card */}
          <div className="enhanced-card">
            <div className="enhanced-card-header">
              <h3 className="section-header">
                <FiFileText className="section-icon text-blue-600" />
                Order Information
              </h3>
            </div>
            <div className="enhanced-card-body">
              <div className="info-row">
                <span className="info-label">
                  <FiUser className="info-icon" />
                  Customer Name
                </span>
                <span className="info-value">{orderDetails?.customerName || 'Guest'}</span>
              </div>

              {orderDetails?.refundReason && (
                <div className="info-row">
                  <span className="info-label">Refund Reason</span>
                  <span
                    className="info-value"
                    style={{
                      color: '#dc3545',
                      backgroundColor: '#f8d7da',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontWeight: '500',
                    }}
                  >
                    {orderDetails?.refundReason}
                  </span>
                </div>
              )}

              <div className="info-row">
                <span className="info-label">
                  <FiCreditCard className="info-icon" />
                  Payment Type
                </span>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {(paymentMethods.length ? paymentMethods : ['Unknown']).map((m) => {
                    const p = getPaymentTypeDisplay(m);
                    return (
                      <div key={String(m)} className={`payment-type ${p.className}`}>
                        <span>{p.icon}</span>
                        <span>{p.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="info-row">
                <span className="info-label">Order No</span>
                <span className="info-value">{orderDetails?.id || '0'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Invoice Number</span>
                <span className="info-value">{orderDetails?.invoiceNumber || 'N/A'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Order Status</span>
                <span className={`status-badge ${orderStatus.className}`}>{orderStatus.text}</span>
              </div>
            </div>
          </div>

          {/* Customer Details Card */}
          <div className="enhanced-card">
            <div className="enhanced-card-header">
              <h3 className="section-header">
                <FiUser className="section-icon text-green-600" />
                Customer Details
              </h3>
            </div>
            <div className="enhanced-card-body">
              <div className="info-row">
                <span className="info-label">
                  <FiUser className="info-icon" />
                  Customer
                </span>
                <span className="info-value">{orderDetails?.customerName || 'Guest'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Customer Type</span>
                <span className="info-value">{orderDetails?.customerType === 0 ? 'Guest' : 'Registered'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Address</span>
                <span className="info-value">{orderDetails?.address1 || '---'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Mobile No</span>
                <span className="info-value">{orderDetails?.mobileNumber || '---'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Email</span>
                <span className="info-value">{orderDetails?.customerEmail || '---'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(CashOrderDetails);
